/**
 * AnalysisDetailModal - Reusable detail modal for interaction analyses
 * Tabbed: Overview | Transcript | Coaching | QA Report
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { X, Phone, MessageSquare, Send, Trash2, MessageCircle, FileText, BookOpen, ClipboardCheck, CheckCircle, AlertTriangle, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, Lightbulb, Award } from 'lucide-react';
import toast from 'react-hot-toast';
import SentimentBadge from './SentimentBadge';
import LocationPill from './LocationPill';
import { PermissionGate } from '@shared/auth';
import { StandardPermission } from '@shared/auth/permissions';
import { toTitleCase, typeLabel, sentimentToPercent } from '../../utils/formatters';
import { getInteractionTranscript } from '../../services/analyticsService';
import {
  getCoachingNotes,
  createCoachingNote,
  deleteCoachingNote,
} from '../../services/coachingService';
import { getQAScore } from '../../services/qaService';
import useFocusTrap from '../../hooks/useFocusTrap';
import ConfirmDialog from '../shared/ConfirmDialog';
import AgentName from '../shared/AgentName';

const PRIORITY_DOT = {
  high: 'bg-red-500',
  urgent: 'bg-red-600',
  medium: 'bg-yellow-500',
  low: 'bg-gray-400',
};

const CATEGORY_COLORS = {
  emotional: 'bg-purple-100 text-purple-700',
  billing: 'bg-green-100 text-green-700',
  risk: 'bg-red-100 text-red-700',
  commercial: 'bg-blue-100 text-blue-700',
  product: 'bg-orange-100 text-orange-700',
  operational: 'bg-yellow-100 text-yellow-700',
  security: 'bg-pink-100 text-pink-700',
  positive: 'bg-emerald-100 text-emerald-700',
  customer_request: 'bg-cyan-100 text-cyan-700',
};

const QA_SUB_PARAMETERS = {
  101: { name: 'Welcome Script', category: 'Call Opening' },
  102: { name: 'Authentication', category: 'Call Opening' },
  103: { name: 'Personalization', category: 'Call Opening' },
  104: { name: 'Understanding Query', category: 'Call Opening' },
  105: { name: 'Avoiding Interruptions', category: 'Call Opening' },
  106: { name: 'Acknowledging Customer', category: 'Call Opening' },
  107: { name: 'Avoiding Dead Air', category: 'Call Opening' },
  201: { name: 'Tone/Pitch/Clarity', category: 'Voice/Communication' },
  202: { name: 'Pronunciation/Grammar', category: 'Voice/Communication' },
  203: { name: 'Empathy/Apology', category: 'Voice/Communication' },
  204: { name: 'Professionalism', category: 'Voice/Communication' },
  205: { name: 'Not Rushing', category: 'Voice/Communication' },
  206: { name: 'Courtesy', category: 'Voice/Communication' },
  207: { name: 'Objection Handling', category: 'Voice/Communication' },
  301: { name: 'Asking Proper Questions', category: 'Probing Skills' },
  401: { name: 'Complete/Accurate Info', category: 'Product Knowledge' },
  501: { name: 'No Unnecessary Hold', category: 'FTR' },
  502: { name: 'Hold Protocol', category: 'FTR' },
  503: { name: 'System Check', category: 'FTR' },
  504: { name: 'Escalation Procedure', category: 'FTR' },
  601: { name: 'Correct Log Entry', category: 'Call Logging' },
  602: { name: 'Reference Number', category: 'Call Logging' },
  701: { name: 'Missed Call Info', category: 'Proactive Closing' },
  702: { name: 'Offered Assistance', category: 'Proactive Closing' },
  703: { name: 'Proper Closing', category: 'Proactive Closing' },
};

const QUALITY_BADGE = {
  Excellent: 'bg-green-100 text-green-700',
  Good: 'bg-blue-100 text-blue-700',
  Average: 'bg-yellow-100 text-yellow-700',
  'Below Average': 'bg-orange-100 text-orange-700',
  Poor: 'bg-red-100 text-red-700',
  'Auto-Fail': 'bg-red-200 text-red-900 ring-1 ring-red-400',
};

const SOURCE_BADGE = {
  deterministic: { label: 'Audio Analysis', className: 'bg-cyan-50 text-cyan-700' },
  llm: { label: 'AI Scored', className: 'bg-violet-50 text-violet-700' },
  audio_analysis: { label: 'Audio Analysis', className: 'bg-cyan-50 text-cyan-700' },
  insufficient_data: { label: 'Insufficient Data', className: 'bg-gray-100 text-gray-500' },
  not_applicable: { label: 'N/A', className: 'bg-slate-100 text-slate-500' },
};

const SectionLabel = ({ children }) => (
  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{children}</span>
);

const MiniBar = ({ label, value }) => {
  const pct = Math.round((value || 0) * 100);
  const color = pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-yellow-500' : 'bg-red-500';
  return (
    <div className="flex items-center space-x-2 text-sm">
      <span className="text-gray-600 w-28">{label}</span>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label={label} />
      </div>
      <span className="text-gray-700 font-medium w-10 text-right">{pct}%</span>
    </div>
  );
};

const TABS = [
  { key: 'overview', label: 'Overview', icon: FileText },
  { key: 'transcript', label: 'Transcript', icon: MessageCircle },
  { key: 'coaching', label: 'Coaching', icon: BookOpen },
  { key: 'qa_report', label: 'QA Report', icon: ClipboardCheck },
];

const STATUS_BADGE = {
  pending: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  completed: 'bg-green-100 text-green-800',
  dismissed: 'bg-gray-100 text-gray-600',
};

/* ─── Transcript Tab ─── */
const TranscriptTab = ({ interactionId, onQuoteToCoaching }) => {
  const [loading, setLoading] = useState(true);
  const [transcript, setTranscript] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      const result = await getInteractionTranscript(interactionId);
      if (cancelled) return;
      if (result.success) {
        setTranscript(result.data?.transcript_data || null);
      }
      setLoading(false);
    };
    fetch();
    return () => { cancelled = true; };
  }, [interactionId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
      </div>
    );
  }

  const messages = transcript?.messages || [];
  if (messages.length === 0) {
    return (
      <div className="py-12 text-center">
        <MessageCircle size={28} className="mx-auto text-gray-300 mb-2" />
        <p className="text-sm text-gray-400">No transcript available for this interaction.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {messages.map((msg, i) => {
        const isAgent = (msg.speaker || '').toLowerCase().includes('agent') || (msg.speaker || '').toLowerCase().includes('bot');
        return (
          <div key={i} className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
            <div className={`group max-w-[75%] rounded-lg px-3.5 py-2.5 ${
              isAgent
                ? 'bg-primary-50 border border-primary-100'
                : 'bg-gray-50 border border-gray-100'
            }`}>
              <div className="flex items-center space-x-2 mb-1">
                <span className={`text-[11px] font-semibold ${isAgent ? 'text-primary-600' : 'text-gray-500'}`}>
                  {msg.speaker || (isAgent ? 'Agent' : 'Customer')}
                </span>
                {msg.timestamp && (
                  <span className="text-[10px] text-gray-500">
                    {(() => {
                      const d = new Date(msg.timestamp);
                      return isNaN(d.getTime()) ? msg.timestamp : d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                    })()}
                  </span>
                )}
                {msg.sentiment && (
                  <span className={`w-1.5 h-1.5 rounded-full ${
                    msg.sentiment === 'positive' ? 'bg-green-500'
                    : msg.sentiment === 'negative' ? 'bg-red-500'
                    : 'bg-gray-300'
                  }`} />
                )}
                {onQuoteToCoaching && (
                  <button
                    onClick={() => onQuoteToCoaching(msg.speaker || '', msg.timestamp || '', msg.text || '')}
                    className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 p-0.5 text-gray-300 hover:text-primary-500 focus:text-primary-500 transition-all focus:outline-none focus:ring-1 focus:ring-primary-300 rounded"
                    title="Quote in coaching note"
                  >
                    <BookOpen size={11} />
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{msg.text}</p>
            </div>
          </div>
        );
      })}
      {transcript?.source && (
        <p className="text-[10px] text-gray-400 text-center pt-2">Source: {transcript.source}</p>
      )}
    </div>
  );
};

/* ─── Coaching Tab ─── */
const CoachingTab = ({ analysis, noteText, onNoteTextChange }) => {
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchNotes = useCallback(async () => {
    setLoading(true);
    const result = await getCoachingNotes({ interaction_id: analysis.interaction_id, page_size: 50 });
    if (result.success) {
      setNotes(result.data?.notes || []);
    }
    setLoading(false);
  }, [analysis.interaction_id]);

  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleSubmit = async () => {
    const trimmed = noteText.trim();
    if (!trimmed) return;
    setSubmitting(true);
    const result = await createCoachingNote({
      interaction_id: analysis.interaction_id,
      agent_id: analysis.agent_id || null,
      note_text: trimmed,
    });
    if (result.success) {
      toast.success('Coaching note added');
      onNoteTextChange('');
      fetchNotes();
    } else {
      toast.error(result.error || 'Failed to add note');
    }
    setSubmitting(false);
  };

  const handleDelete = async (noteId) => {
    const result = await deleteCoachingNote(noteId);
    if (result.success) {
      toast.success('Note deleted');
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } else {
      toast.error(result.error || 'Failed to delete note');
    }
    setDeleteTarget(null);
  };

  return (
    <div className="space-y-4">
      {/* Add Note — the whole composer is gated, not just the button, so a
          read-only viewer is never given a textarea the API would refuse. */}
      <PermissionGate permission={StandardPermission.Analytics.COACHING}>
        <div className="flex items-start space-x-2">
          <textarea
            value={noteText}
            onChange={(e) => onNoteTextChange(e.target.value)}
            placeholder="Add a coaching note for this interaction..."
            rows={2}
            className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-primary-300 resize-none"
          />
          <button
            onClick={handleSubmit}
            disabled={!noteText.trim() || submitting}
            className="p-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label="Send coaching note"
          >
            <Send size={16} />
          </button>
        </div>
      </PermissionGate>

      {/* Notes List */}
      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500" />
        </div>
      ) : notes.length === 0 ? (
        <div className="py-8 text-center">
          <BookOpen size={24} className="mx-auto text-gray-300 mb-2" />
          <p className="text-sm text-gray-400">No coaching notes yet. Add one above.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {notes.map((note) => (
            <div key={note.id} className="bg-gray-50 rounded-lg px-4 py-3 group">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center space-x-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${STATUS_BADGE[note.status] || STATUS_BADGE.pending}`}>
                    {toTitleCase(note.status)}
                  </span>
                  {note.coach_user_id && (
                    <span className="text-[10px] text-gray-500">{note.coach_user_id}</span>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-[10px] text-gray-500">
                    {note.created_at ? new Date(note.created_at).toLocaleString() : ''}
                  </span>
                  <PermissionGate permission={StandardPermission.Analytics.COACHING}>
                    <button
                      onClick={() => setDeleteTarget(note.id)}
                      className="opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 focus:opacity-100 p-1.5 text-gray-400 hover:text-red-500 focus:text-red-500 transition-all focus:outline-none focus:ring-1 focus:ring-red-300 rounded"
                      aria-label="Delete coaching note"
                    >
                      <Trash2 size={12} />
                    </button>
                  </PermissionGate>
                </div>
              </div>
              <p className="text-sm text-gray-700">{note.note_text}</p>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onConfirm={() => handleDelete(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        title="Delete coaching note"
        message="This coaching note will be permanently deleted. This action cannot be undone."
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
};

/* ─── QA Report Tab ─── */
const QAReportTab = ({ interactionId }) => {
  const [loading, setLoading] = useState(true);
  const [qaScore, setQaScore] = useState(null);
  const [notScored, setNotScored] = useState(false);
  const [error, setError] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({ ...prev, [category]: !prev[category] }));
  };

  useEffect(() => {
    if (!interactionId) {
      setNotScored(true);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const fetch = async () => {
      setLoading(true);
      setNotScored(false);
      setError(null);
      const result = await getQAScore(interactionId);
      if (cancelled) return;
      if (result.success) {
        setQaScore(result.data);
      } else if (result.error === 'QA score not found') {
        setNotScored(true);
      } else {
        setError(result.error);
      }
      setLoading(false);
    };
    fetch();
    return () => { cancelled = true; };
  }, [interactionId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500" />
      </div>
    );
  }

  if (notScored) {
    return (
      <div className="py-12 text-center">
        <ClipboardCheck size={28} className="mx-auto text-gray-300 mb-2" />
        <p className="text-sm text-gray-400">No QA score available for this interaction.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center">
        <AlertTriangle size={28} className="mx-auto text-red-300 mb-2" />
        <p className="text-sm text-red-500">{error}</p>
      </div>
    );
  }

  if (!qaScore) return null;

  const subParams = qaScore.sub_parameter_scores || [];
  const strengths = qaScore.strengths || [];
  const improvements = qaScore.improvements || [];
  const totalScore = qaScore.total_score_100;
  const qualityCategory = qaScore.quality_category;

  const groupedParams = {};
  for (const sp of subParams) {
    const meta = QA_SUB_PARAMETERS[sp.parameter_id];
    if (!meta) continue;
    const cat = meta.category;
    if (!groupedParams[cat]) groupedParams[cat] = [];
    groupedParams[cat].push({ ...sp, name: meta.name });
  }

  const circumference = 2 * Math.PI * 58;
  const scoreColor = totalScore >= 80 ? '#22c55e' : totalScore >= 60 ? '#3b82f6' : totalScore >= 40 ? '#f59e0b' : '#ef4444';

  const categoryAverages = {};
  for (const [cat, params] of Object.entries(groupedParams)) {
    const applicable = params.filter(p => p.applicable !== false);
    const total = applicable.reduce((sum, p) => sum + (p.max_score > 0 ? p.score / p.max_score : 0), 0);
    categoryAverages[cat] = applicable.length > 0 ? Math.round((total / applicable.length) * 100) : 0;
  }

  return (
    <div className="space-y-5">
      {/* ── Hero Score ── */}
      {totalScore != null && (
        <div className="flex flex-col items-center pt-2 pb-4">
          <svg className="w-28 h-28 sm:w-[140px] sm:h-[140px]" viewBox="0 0 140 140" role="img" aria-label={`QA Score: ${Math.round(totalScore)} out of 100`}>
            <circle cx="70" cy="70" r="58" fill="none" stroke="#e5e7eb" strokeWidth="10" />
            <circle
              cx="70" cy="70" r="58" fill="none"
              stroke={scoreColor} strokeWidth="10" strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={circumference * (1 - Math.round(totalScore) / 100)}
              transform="rotate(-90 70 70)"
              style={{ transition: 'stroke-dashoffset 0.6s ease' }}
            />
            <text x="70" y="66" textAnchor="middle" fontSize="32" fontWeight="bold" fill={scoreColor}>
              {Math.round(totalScore)}
            </text>
            <text x="70" y="84" textAnchor="middle" fontSize="12" fill="#9ca3af">/ 100</text>
          </svg>
          {qualityCategory && (
            <span className={`mt-2 text-xs px-3 py-1 rounded-full font-semibold ${QUALITY_BADGE[qualityCategory] || 'bg-gray-100 text-gray-600'}`}>
              {qualityCategory}
            </span>
          )}
        </div>
      )}

      {/* ── Fatal Error Banner ── */}
      {qaScore.is_auto_fail && qaScore.fatal_errors?.length > 0 && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-4">
          <div className="flex items-center space-x-2 mb-2">
            <AlertTriangle size={16} className="text-red-600" />
            <span className="text-sm font-bold text-red-800">Fatal Error — Auto-Fail</span>
          </div>
          <ul className="space-y-1.5">
            {qaScore.fatal_errors.map((fe, i) => (
              <li key={i} className="text-sm text-red-700">
                <span className="font-medium">{fe.type}:</span> {fe.description}
                {fe.evidence_quote && (
                  <p className="text-xs text-red-500 italic mt-0.5 border-l-2 border-red-300 pl-2">"{fe.evidence_quote}"</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ── Sub-Parameters (Accordion) ── */}
      {Object.keys(groupedParams).length > 0 && (
        <div>
          <SectionLabel>Sub-Parameter Scores</SectionLabel>
          <div className="mt-2 space-y-2">
            {Object.entries(groupedParams).map(([category, params]) => {
              const isOpen = !!expandedCategories[category];
              const avg = categoryAverages[category];
              const avgColor = avg >= 70 ? 'bg-green-100 text-green-700' : avg >= 40 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700';
              return (
                <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    onClick={() => toggleCategory(category)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                    aria-expanded={isOpen}
                    aria-controls={`qa-cat-${category.replace(/\s+/g, '-')}`}
                  >
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-semibold text-gray-700">{category}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${avgColor}`}>{avg}%</span>
                    </div>
                    {isOpen ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                  </button>
                  {isOpen && (
                    <div id={`qa-cat-${category.replace(/\s+/g, '-')}`} className="px-4 py-2 space-y-2">
                      {params.map((p) => {
                        const pct = p.max_score > 0 ? Math.round((p.score / p.max_score) * 100) : 0;
                        const barColor = pct >= 70 ? 'bg-green-500' : pct >= 40 ? 'bg-yellow-500' : 'bg-red-500';
                        const chipColor = pct >= 70 ? 'bg-green-50 text-green-700' : pct >= 40 ? 'bg-yellow-50 text-yellow-700' : 'bg-red-50 text-red-700';
                        const isNA = p.applicable === false;
                        const srcBadge = isNA
                          ? SOURCE_BADGE.not_applicable
                          : (SOURCE_BADGE[p.source] || SOURCE_BADGE[p.evidence_speaker] || null);
                        return (
                          <div key={p.parameter_id} className={isNA ? 'opacity-50' : ''}>
                            <div className="flex items-center space-x-3 text-sm">
                              <span className={`w-44 flex-shrink-0 ${isNA ? 'text-gray-400' : 'text-gray-600'}`}>{p.name}</span>
                              <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden" role="progressbar" aria-valuenow={isNA ? 0 : pct} aria-valuemin={0} aria-valuemax={100} aria-label={p.name}>
                                {!isNA && <div className={`h-full rounded-full ${barColor}`} style={{ width: `${pct}%` }} />}
                              </div>
                              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full flex-shrink-0 ${isNA ? 'bg-slate-50 text-slate-400' : chipColor}`}>
                                {isNA ? 'N/A' : `${p.score}/${p.max_score}`}
                              </span>
                              {srcBadge && (
                                <span className={`text-[9px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${srcBadge.className}`}>
                                  {srcBadge.label}
                                </span>
                              )}
                            </div>
                            {p.comments && (
                              <p className={`text-xs mt-1 ml-0 sm:ml-[188px] ${isNA ? 'text-gray-400 italic' : 'text-gray-500'}`}>{p.comments}</p>
                            )}
                            {!isNA && p.evidence_quote && (
                              <div className="mt-1 ml-0 sm:ml-[188px] border-l-2 border-gray-300 pl-2">
                                <p className="text-xs text-gray-500 italic">
                                  "{p.evidence_quote}"
                                  {p.evidence_speaker && p.evidence_speaker !== 'audio_analysis' && (
                                    <span className="not-italic text-gray-400"> — {p.evidence_speaker}</span>
                                  )}
                                </p>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Strengths & Improvements ── */}
      {(strengths.length > 0 || improvements.length > 0) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="border border-green-200 bg-green-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <CheckCircle size={16} className="text-green-600" />
              <span className="text-sm font-semibold text-green-800">Strengths</span>
            </div>
            {strengths.length > 0 ? (
              <ul className="space-y-2">
                {strengths.map((s, i) => (
                  <li key={i} className="flex items-start space-x-2 text-sm text-green-900">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-green-500 flex-shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-green-600 italic">No strengths identified.</p>
            )}
          </div>
          <div className="border border-amber-200 bg-amber-50 rounded-lg p-4">
            <div className="flex items-center space-x-2 mb-3">
              <AlertTriangle size={16} className="text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">Areas for Improvement</span>
            </div>
            {improvements.length > 0 ? (
              <ul className="space-y-2">
                {improvements.map((s, i) => (
                  <li key={i} className="flex items-start space-x-2 text-sm text-amber-900">
                    <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-xs text-amber-600 italic">No improvements identified.</p>
            )}
          </div>
        </div>
      )}

      {/* ── Coaching Recommendation ── */}
      {qaScore.coaching_note && (
        <div className="flex items-start space-x-3 border-l-4 border-indigo-400 bg-indigo-50 rounded-r-lg px-4 py-3">
          <Lightbulb size={18} className="text-indigo-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wider mb-1">Coaching Recommendation</p>
            <p className="text-sm text-indigo-900 leading-relaxed">{qaScore.coaching_note}</p>
          </div>
        </div>
      )}

      {/* ── Metadata Footer ── */}
      <div className="flex items-center justify-between pt-3 mt-2 border-t border-gray-100">
        {qaScore.analysis_tier && (
          <span className="flex items-center space-x-1 text-[10px] text-gray-400">
            <Award size={10} />
            <span>Scored by: {qaScore.analysis_tier === 'llm' ? 'AI' : toTitleCase(qaScore.analysis_tier)}</span>
          </span>
        )}
        {qaScore.analyzed_at && (
          <span className="text-[10px] text-gray-400">
            {new Date(qaScore.analyzed_at).toLocaleString()}
          </span>
        )}
      </div>
    </div>
  );
};

const DISPOSITION_BADGE = {
  resolved: 'bg-green-100 text-green-700',
  escalated: 'bg-red-100 text-red-700',
  transferred: 'bg-blue-100 text-blue-700',
  abandoned: 'bg-gray-100 text-gray-600',
  follow_up_required: 'bg-yellow-100 text-yellow-700',
  pending: 'bg-gray-50 text-gray-500',
};

/* ─── Overview Tab (existing modal body) ─── */
const OverviewTab = ({ analysis }) => {
  const signals = analysis.signals || [];
  const aspects = analysis.aspects || [];
  const agentQuality = analysis.agent_quality;
  const riskFactors = analysis.risk_factors || [];
  const actions = analysis.recommended_actions || [];
  const summary = analysis.summary;
  const contactReason = analysis.contact_reason;
  const disposition = analysis.disposition;
  const autoTags = analysis.tags || [];
  const keyEmotions = analysis.key_emotions || [];

  return (
    <div className="space-y-4">
      {/* Summary */}
      {summary && (
        <div>
          <SectionLabel>Summary</SectionLabel>
          <div className="bg-gray-50 rounded-lg px-4 py-3 mt-1.5">
            <p className="text-sm text-gray-800 leading-relaxed">{summary}</p>
          </div>
        </div>
      )}

      {/* Disposition */}
      {disposition && (
        <div>
          <SectionLabel>Outcome</SectionLabel>
          <div className="mt-1.5">
            <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${DISPOSITION_BADGE[disposition] || DISPOSITION_BADGE.pending}`}>
              {toTitleCase(disposition)}
            </span>
          </div>
        </div>
      )}

      {/* Reason + Tags */}
      {(contactReason || autoTags.length > 0) && (
        <div>
          <SectionLabel>Reason &amp; Tags</SectionLabel>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {contactReason && (
              <span className="text-xs px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 font-medium">
                {toTitleCase(contactReason)}
              </span>
            )}
            {autoTags.map((tag, i) => (
              <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                {toTitleCase(tag)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Key Emotions */}
      {keyEmotions.length > 0 && (
        <div>
          <SectionLabel>Emotions Detected</SectionLabel>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {keyEmotions.map((emotion, i) => (
              <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-violet-50 text-violet-700">
                {toTitleCase(emotion)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Signals */}
      {signals.length > 0 && (
        <div>
          <SectionLabel>Signals Detected</SectionLabel>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {signals.map((s, i) => (
              <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">
                {toTitleCase(s.signal_name)}
                {s.signal_category && (
                  <span className={`ml-1.5 text-[10px] px-1 py-0.5 rounded ${CATEGORY_COLORS[s.signal_category] || 'bg-gray-100 text-gray-600'}`}>
                    {toTitleCase(s.signal_category)}
                  </span>
                )}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Topics (aspects) */}
      {aspects.length > 0 && (
        <div>
          <SectionLabel>Topics</SectionLabel>
          <div className="flex flex-wrap items-center gap-2 mt-1.5">
            {aspects.map((a, i) => (
              <span key={i} className={`text-xs px-2.5 py-1 rounded-full ${
                a.sentiment === 'positive' ? 'bg-green-50 text-green-700'
                : a.sentiment === 'negative' ? 'bg-red-50 text-red-700'
                : 'bg-gray-50 text-gray-600'
              }`}>
                {toTitleCase(a.aspect)}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Agent Quality — dynamic dimensions */}
      {agentQuality && Object.keys(agentQuality).length > 0 && (
        <div>
          <SectionLabel>Agent Quality</SectionLabel>
          <div className="space-y-2 mt-2">
            {Object.entries(agentQuality).map(([key, value]) => (
              <MiniBar key={key} label={toTitleCase(key)} value={value} />
            ))}
          </div>
        </div>
      )}

      {/* Evidence */}
      {signals.some(s => s.evidence) && (
        <div>
          <SectionLabel>Evidence</SectionLabel>
          <div className="space-y-2 mt-1.5">
            {signals.filter(s => s.evidence).map((s, i) => (
              <div key={i} className="bg-gray-50 rounded-lg px-4 py-2">
                <p className="text-xs font-medium text-gray-700 mb-0.5">{toTitleCase(s.signal_name)}</p>
                <p className="text-sm text-gray-600 italic">&ldquo;{s.evidence}&rdquo;</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Risk Factors */}
      {riskFactors.length > 0 && (
        <div>
          <SectionLabel>Risk Factors</SectionLabel>
          <div className="bg-red-50 rounded-lg px-4 py-3 mt-1.5 space-y-1">
            {riskFactors.map((r, i) => (
              <div key={i} className="flex items-center space-x-2 text-sm text-red-800">
                <span className="font-semibold uppercase text-xs">[{r.severity}]</span>
                <span>{r.factor}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recommended Actions */}
      {actions.length > 0 && (
        <div>
          <SectionLabel>Recommended Actions</SectionLabel>
          <div className="space-y-1.5 mt-1.5">
            {actions.map((a, i) => (
              <div key={i} className="flex items-center space-x-2 text-sm">
                <span className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_DOT[a.priority] || 'bg-gray-400'}`} />
                <span className="text-gray-700">{a.action}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Main Modal ─── */
const AnalysisDetailModal = ({ analysis, onClose, hasPrev = false, hasNext = false, onNavigate, location }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [coachingNoteText, setCoachingNoteText] = useState('');
  const modalRef = useRef(null);
  useFocusTrap(modalRef);

  useEffect(() => {
    const tabKeys = TABS.map((t) => t.key);
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      const onTab = document.activeElement?.getAttribute('role') === 'tab';
      if (onTab) {
        const idx = tabKeys.indexOf(activeTab);
        if (e.key === 'ArrowRight') {
          const next = tabKeys[(idx + 1) % tabKeys.length];
          setActiveTab(next);
          document.getElementById(`tab-${next}`)?.focus();
        } else if (e.key === 'ArrowLeft') {
          const prev = tabKeys[(idx - 1 + tabKeys.length) % tabKeys.length];
          setActiveTab(prev);
          document.getElementById(`tab-${prev}`)?.focus();
        }
      }
      if (!onTab && onNavigate) {
        if (e.key === 'ArrowLeft' && hasPrev) onNavigate(-1);
        if (e.key === 'ArrowRight' && hasNext) onNavigate(1);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, activeTab, setActiveTab, hasPrev, hasNext, onNavigate]);

  const handleQuoteToCoaching = useCallback((speaker, timestamp, text) => {
    const snippet = text.length > 80 ? text.slice(0, 80) + '...' : text;
    const quote = timestamp
      ? `Re: ${speaker} at ${timestamp} — "${snippet}"\n\n`
      : `Re: ${speaker} — "${snippet}"\n\n`;
    setCoachingNoteText(prev => prev ? prev + '\n' + quote : quote);
    setActiveTab('coaching');
  }, []);

  if (!analysis) return null;

  const sentiment = analysis.overall_sentiment || 'neutral';
  const lang = analysis.language;
  const TypeIcon = analysis.interaction_type === 'call' ? Phone : MessageSquare;
  const scorePct = sentimentToPercent(analysis.sentiment_score);

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40 transition-opacity duration-300" />

      {/* Drawer */}
      <div
        ref={modalRef}
        className="fixed right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl flex flex-col transition-transform duration-300"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
      >
        {/* Header */}
        <div className="border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center space-x-3">
            <TypeIcon size={16} className="text-gray-400" />
            <span id="modal-title" className="text-sm font-mono text-gray-500">#{analysis.interaction_id}</span>
            <SentimentBadge sentiment={sentiment} />
            <span className="text-sm font-medium text-gray-500">{scorePct}/100</span>
            {lang && lang !== 'en' && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-mono uppercase">{lang}</span>
            )}
            <LocationPill location={location} variant="detail" />
          </div>
          <div className="flex items-center space-x-1">
            {onNavigate && (
              <>
                <button
                  onClick={() => onNavigate(-1)}
                  disabled={!hasPrev}
                  className="p-2.5 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Previous conversation"
                >
                  <ChevronLeft size={16} className="text-gray-500" />
                </button>
                <button
                  onClick={() => onNavigate(1)}
                  disabled={!hasNext}
                  className="p-2.5 rounded-full hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                  aria-label="Next conversation"
                >
                  <ChevronRight size={16} className="text-gray-500" />
                </button>
              </>
            )}
            <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 transition-colors" aria-label="Close">
              <X size={18} className="text-gray-400" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 px-4 sm:px-6 flex space-x-1 flex-shrink-0" role="tablist">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                role="tab"
                id={`tab-${tab.key}`}
                aria-selected={isActive}
                aria-controls={`tabpanel-${tab.key}`}
                tabIndex={isActive ? 0 : -1}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center space-x-1.5 px-3 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                  isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon size={14} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Metadata (always visible) */}
        <div className="flex items-center flex-wrap gap-x-4 gap-y-1 px-4 sm:px-6 pt-4 pb-2 text-xs text-gray-500 flex-shrink-0">
          <span>
            {(analysis.occurred_at || analysis.analyzed_at)
              ? new Date(analysis.occurred_at || analysis.analyzed_at).toLocaleString()
              : 'N/A'}
          </span>
          <span className="flex items-center gap-1.5">
            <span className="px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 font-medium">
              {toTitleCase(typeLabel(analysis.source_type || 'platform'))}
            </span>
            {analysis.source_id && analysis.source_id !== analysis.interaction_id && (
              <span className="font-mono">{analysis.source_id}</span>
            )}
          </span>
          {analysis.agent_id && (
            <span>Agent: <AgentName id={analysis.agent_id} /></span>
          )}
        </div>

        {/* Tab content (scrollable) */}
        <div className="px-4 sm:px-6 pb-5 overflow-y-auto flex-1" role="tabpanel" id={`tabpanel-${activeTab}`} aria-labelledby={`tab-${activeTab}`}>
          {activeTab === 'overview' && <OverviewTab analysis={analysis} />}
          {activeTab === 'transcript' && <TranscriptTab interactionId={analysis.interaction_id} onQuoteToCoaching={handleQuoteToCoaching} />}
          {activeTab === 'coaching' && <CoachingTab analysis={analysis} noteText={coachingNoteText} onNoteTextChange={setCoachingNoteText} />}
          {activeTab === 'qa_report' && <QAReportTab interactionId={analysis.interaction_id} />}
        </div>
      </div>
    </div>
  );
};

export { SectionLabel, MiniBar, PRIORITY_DOT, CATEGORY_COLORS };
export default AnalysisDetailModal;
